import { Skeleton, Typography } from 'antd';
import React, { FC } from 'react';
import styled from 'styled-components';
import { Channel, Configuration } from 'api/notifications';
import { EventType } from 'api/notifications/meta';
import { Main } from 'components/atoms/main';
import { ProductiveHeading07, Text02 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useAccountDataQuery } from 'queries/account';
import { useConfigurationsStore } from 'stores/notifications/use-configurations-store';
import { Accounts } from './accounts';
import { Dates } from './dates';
import { Documents } from './documents';
import { Everything } from './everything';
import { Forms } from './forms';
import { CommonProps } from './group';
import { NotificationContextProvider } from './hooks';
import { Messages } from './messages';
import { Phases } from './phase';
import { Projects } from './project';
import { ProjectUpdates } from './project-updates';
import { Reminders } from './reminders';
import { Tasks } from './tasks';

const Title = styled.div`
  ${ProductiveHeading07};
  ${Text02};
  padding-bottom: var(--spacing-03);
`;
const { Paragraph } = Typography;

export const NotificationsConfiguration: FC<{
  className?: string;
  customerConfig?: boolean;
}> = ({ className, customerConfig }) => {
  const { content, update } = useConfigurationsStore(customerConfig);
  const { data } = useAccountDataQuery();
  const onChange = (configuration: Configuration) => {
    update.current(configuration);
  };

  const props: CommonProps = {
    configurations: content[1] ?? [],
    mandatory: data?.account.metaData.canNotificationsConfigBeEdited
      ? []
      : [
          // Note: slack notifications should always be editable unless that notification type is not supported by slack
          ...[
            EventType.UserMentionedInTask,
            EventType.UserMentionedInDocument,
            EventType.UserMentionedInMessage,
            EventType.MessageReplyAdded,
            EventType.TaskAssignedToUser,
          ].map((eventType) => ({
            eventType,
            channels: {
              [Channel.InApp]: customerConfig ?? false, // Mandatory for customers but not for vendors
              [Channel.Email]: customerConfig ?? false,
              [Channel.Slack]: false,
            },
          })),
          {
            eventType: EventType.ProjectStatusUpdatePublished,
            channels: {
              [Channel.InApp]: false,
              [Channel.Email]: true, // This is mandatory, because it is a part of the feature itself
              [Channel.Slack]: false,
            },
          },
          {
            eventType: EventType.ProjectStatusChanged,
            channels: {
              [Channel.InApp]: customerConfig ?? false,
              [Channel.Email]: false,
              [Channel.Slack]: false,
            },
          },
          {
            eventType: EventType.UserAddedToProject,
            channels: {
              [Channel.InApp]: customerConfig ?? false,
              [Channel.Email]: customerConfig ?? false, // This does not make sense for customers so it is mandatory (true by default) for them
              [Channel.Slack]: true, // This notification does not support slack (false by default)
            },
          },
        ],
    onChange,
  };

  const title = useTranslation('notifications.configuration.customer.heading');

  const description = useTranslation(
    'notifications.configuration.customer.description',
  );

  return (
    <Main className={className}>
      {customerConfig ? (
        <div>
          <Title>{title}</Title>
          <Paragraph>{description}</Paragraph>
        </div>
      ) : null}

      <NotificationContextProvider isCustomerPortal={customerConfig}>
        <Everything />
        {content[0] === 'loading' ? <Skeleton active /> : null}
        {content[0] === 'loaded' ? (
          <>
            <Messages {...props} />
            <Tasks {...props} />
            <Forms {...props} />
            <Documents {...props} />
            <Phases {...props} />
            <Projects {...props} />
            <Accounts {...props} />
            <ProjectUpdates {...props} />
            <Dates {...props} />
            <Reminders {...props} />
          </>
        ) : null}
      </NotificationContextProvider>
    </Main>
  );
};
